<script setup lang="tsx">
import type { DocTreeNode } from '@/models/api-generated'
import { ref, nextTick, computed, watchEffect } from 'vue'
import { isEditingAny, selectedFolder } from './shared'
import { viewing } from '@/services/utils'

const props = defineProps<{
	level?: number
	selectionType?: 'all' | 'document' | 'url'
}>()

const model = defineModel<DocTreeNode>({ required: true })

const emits = defineEmits<{
	(e: 'select-folder', node: DocTreeNode): void
	(e: 'folder-renamed', node: DocTreeNode, newPath: string, newName: string): void
}>()

const isEditing = ref(false)
const editedName = ref(model.value.name)
const thisNode = ref<HTMLElement>()

const showDocuments = computed(() => props.selectionType !== 'url')
const showUrls = computed(() => props.selectionType !== 'document')
const hasVisibleEntries = computed(() => {
	const children = model.value.children?.length ?? 0
	const docs = showDocuments.value ? (model.value.documents?.length ?? 0) : 0
	const urls = showUrls.value ? (model.value.urls?.length ?? 0) : 0
	return children > 0 || docs > 0 || urls > 0
})
const isRoot = computed(() => (props.level ?? 0) === 0 || model.value.path === '/')

watchEffect(() => {
	if (isRoot.value) {
		model.value.isOpen = true
	} else if (!model.value.isOpen) {
		model.value.isOpen = false
	}
})

const handleClick = (e: MouseEvent) => {
	if (isEditingAny.value) return

	const el = e.target as HTMLElement
	if (el.closest('.file-node')) return
	if (el.closest('.folder-node') !== thisNode.value) return

	const clickedChevron = Boolean(el.closest('.folder-chevron'))
	if (clickedChevron && !isRoot.value && model.value.isOpen) {
		model.value.isOpen = false
		return
	}

	if (!isRoot.value) model.value.isOpen = !model.value.isOpen
	else model.value.isOpen = true

	emits('select-folder', model.value)
	if (e.detail === 2) startEdit()
}

function startEdit() {
	if (!viewing.value || isEditingAny.value || model.value.path === '/' || (props.level ?? 0) === 0) return

	isEditing.value = true
	isEditingAny.value = true
	editedName.value = model.value.name

	nextTick(() => thisNode.value?.querySelector('input')?.focus())
}

function confirmEdit() {
	if (!editedName.value) return
	if (editedName.value.indexOf('/') >= 0) editedName.value = editedName.value.replaceAll('/', '')

	isEditing.value = false
	isEditingAny.value = false
	if (editedName.value === model.value.name) return
	const oldPath = model.value.path

	const p1 = oldPath.substring(0, oldPath.length - 1)
	const base = p1.substring(0, p1.lastIndexOf('/'))
	const newPath = `${base}/${editedName.value}/`
	emits('folder-renamed', model.value, newPath, editedName.value)
}

function cancelEdit() {
	isEditing.value = false
	isEditingAny.value = false
}
</script>

<template>
	<div
		class="folder-node"
		:class="{ 'is-editing': isEditing }"
		:style="{ paddingLeft: `${Math.min(props.level || 0) + 20}px` }"
		@click.capture="handleClick"
		ref="thisNode"
	>
		<div class="d-flex align-items-center label" :class="{ 'selected-folder': selectedFolder === model }">
			<i
				v-if="!isRoot"
				class="bi me-1 folder-chevron"
				:class="model.isOpen && hasVisibleEntries ? 'bi-chevron-down' : 'bi-chevron-right'"
			></i>
			<i :class="model.isOpen ? 'bi bi-folder2-open' : 'bi bi-folder'"></i>
			<span v-if="!isEditing" :class="['ms-1', { 'fw-bold': isRoot }]">
				{{ model.name || $t('document.label', 2) }}
			</span>
			<input
				v-else
				v-model="editedName"
				class="form-control ms-1"
				@keydown.enter="confirmEdit"
				@keydown.escape="cancelEdit"
				@blur="cancelEdit"
			/>
		</div>
		<!-- <div v-if="model.isOpen && showDocuments && model.documents?.length" class="children">
			<div
				v-for="d in model.documents"
				:key="d.id"
				class="file-node d-flex align-items-center"
				:style="{ paddingLeft: `${(props.level || 0) + 20}px` }"
			>
				<i class="bi bi-file-earmark-text"></i>
				<span class="ms-1">{{ d.name }}</span>
			</div>
		</div>
		<div v-if="model.isOpen && showUrls && model.urls?.length" class="children">
			<div
				v-for="url in model.urls"
				:key="url.id"
				class="file-node d-flex align-items-center"
				:style="{ paddingLeft: `${(props.level || 0) + 20}px` }"
			>
				<i class="bi bi-link-45deg"></i>
				<span class="ms-1">{{ url.url }}</span>
			</div>
		</div> -->
		<div v-if="model.isOpen && model.children?.length" class="children">
			<FolderNode
				v-for="(child, i) in model.children"
				:key="child.path"
				v-model="model.children[i]"
				:level="(props.level || 0) + 1"
				@select-folder="(a) => emits('select-folder', a)"
				@folder-renamed="(a, b, c) => emits('folder-renamed', a, b, c)"
			/>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.folder-node
  cursor: pointer
  &.is-editing, & .file-node
    cursor: initial

:not(.is-editing).label
  &:hover
    color: #007BFF

.selected-folder
  color: #007BFF
  i
    color: #007BFF
</style>
